import { Box, Paper, Typography } from '@mui/material';

interface JSONDiffViewerProps {
  changes: Record<string, any>;
}

/**
 * JSONDiffViewer - Display JSON changes in audit log with syntax highlighting
 */
export const JSONDiffViewer = ({ changes }: JSONDiffViewerProps) => {
  if (!changes || Object.keys(changes).length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No changes recorded
      </Typography>
    );
  }

  return (
    <Box>
      {Object.entries(changes).map(([field, change]) => {
        // Handle different change formats
        const isSimpleChange = change && typeof change === 'object' && 'old' in change && 'new' in change;

        if (isSimpleChange) {
          return (
            <Box key={field} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                {field}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Paper
                  sx={{
                    flex: 1,
                    p: 1,
                    backgroundColor: '#2e1f1f',
                    border: '1px solid #f44336',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="#f44336"
                    fontWeight="bold"
                    display="block"
                  >
                    OLD
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatValue(change.old)}
                  </Typography>
                </Paper>
                <Paper
                  sx={{
                    flex: 1,
                    p: 1,
                    backgroundColor: '#1f2e1f',
                    border: '1px solid #4caf50',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="#4caf50"
                    fontWeight="bold"
                    display="block"
                  >
                    NEW
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatValue(change.new)}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          );
        }

        // Fallback for non-standard change format
        return (
          <Box key={field} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              {field}
            </Typography>
            <Paper sx={{ p: 1, backgroundColor: '#1a2027' }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {formatValue(change)}
              </Typography>
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
};

const formatValue = (value: any): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};
